import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { BookOpen, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const PRODUCTS = [
  { value: "from_diagnosis_to_goodbye", label: "From Diagnosis to Goodbye" },
  { value: "hospice_workbook", label: "Hospice Workbook" },
  { value: "bereavement_workbook", label: "Bereavement Workbook" },
  { value: "multiple", label: "Multiple Resources" },
  { value: "other", label: "Other / Not Sure" },
];
const ORG_TYPES = [
  { value: "hospice", label: "Hospice" },
  { value: "healthcare", label: "Healthcare Organization" },
  { value: "senior_living", label: "Senior Living" },
  { value: "board_and_care", label: "Board & Care" },
  { value: "faith_community", label: "Church / Faith Community" },
  { value: "employer", label: "Employer / Business" },
  { value: "community", label: "Community Organization" },
  { value: "educational", label: "Educational Organization" },
  { value: "other", label: "Other" },
];

const initial = { first_name: "", last_name: "", email: "", organization: "", phone: "", role: "", product: "from_diagnosis_to_goodbye", quantity: "", organization_type: "hospice", message: "" };
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent";

export default function BulkOrderPage() {
  usePageMeta({
    title: "Bulk Order | California Corporate Chaplains",
    description: "Order books, workbooks, and companion guides in bulk for hospices, healthcare organizations, senior living communities, and employers.",
  });
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => {
    const p = params.get("product");
    return { ...initial, product: PRODUCTS.some((x) => x.value === p) ? p : initial.product };
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    const p = params.get("product");
    if (p && PRODUCTS.some((x) => x.value === p)) setForm((s) => ({ ...s, product: p }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await api.post("/bulk-orders", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="bulk-order-success">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-600 mb-6"><CheckCircle2 className="w-7 h-7" /></div>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">Thank you.</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">Your bulk / organizational order request has been received. A member of our team will follow up with pricing and next steps.</p>
          <p className="mt-3 text-sm text-slate-500">This request does not create an order or payment obligation.</p>
          <Link to="/books" className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">Back to Books & Resources</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="bulk-order-page">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3"><BookOpen className="inline w-3.5 h-3.5 mr-1.5" />Bulk / organizational orders</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-tight">Request a Bulk / Organizational Order</h1>
      <p className="mt-4 text-slate-600 leading-relaxed">Tell us what your organization is exploring. We'll follow up with pricing, packages, and organizational options tailored to your team.</p>
      <div className="mt-6 rounded-xl border border-slate-200 bg-[#F8FAFC] p-4 text-sm text-slate-700 leading-relaxed">
        This form is a <strong>request for information / quote</strong>. It does not create an order or payment obligation.
      </div>
      <form onSubmit={submit} data-testid="bulk-order-form" className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5 shadow-sm">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="First name" required><input required data-testid="input-first-name" value={form.first_name} onChange={update("first_name")} className={inputCls} /></Field>
          <Field label="Last name" required><input required data-testid="input-last-name" value={form.last_name} onChange={update("last_name")} className={inputCls} /></Field>
        </div>
        <Field label="Email" required><input required type="email" data-testid="input-email" value={form.email} onChange={update("email")} className={inputCls} /></Field>
        <Field label="Organization name" required><input required data-testid="input-organization" value={form.organization} onChange={update("organization")} className={inputCls} /></Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Phone"><input type="tel" data-testid="input-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
          <Field label="Job title / role"><input data-testid="input-role" value={form.role} onChange={update("role")} className={inputCls} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Resource / product" required>
            <select required data-testid="select-product" value={form.product} onChange={update("product")} className={inputCls}>{PRODUCTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select>
          </Field>
          <Field label="Estimated quantity"><input data-testid="input-quantity" value={form.quantity} onChange={update("quantity")} placeholder="e.g. 50 copies" className={inputCls} /></Field>
        </div>
        <Field label="Organization type" required>
          <select required data-testid="select-organization-type" value={form.organization_type} onChange={update("organization_type")} className={inputCls}>{ORG_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select>
        </Field>
        <Field label="Additional information"><textarea data-testid="textarea-message" rows={4} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} placeholder="Timeline, training interest, or anything else that would help us respond well (optional)." /></Field>
        <button type="submit" disabled={submitting} data-testid="submit-bulk-order" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
          {submitting ? "Sending..." : "Send Request"} <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-slate-500 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5]" /> Encrypted in transit.</p>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label} {required && <span className="text-[#4A6FA5]">*</span>}</span>
      {children}
    </label>
  );
}
